import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Label from '@polestar/component-warehouse-react/components/label/Label';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import Positions from '@polestar/component-warehouse-react/tokens/positions/Positions';
import { useDirection } from '@polestar/component-warehouse-react/contexts/direction';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
/**
 * @internal
 */
export const TooltipLabelId = 'label';
const Tooltip = forwardRef(({ placement = Positions.topCenter, show = true, id, ...props }, ref) => {
    const { isRTL } = useDirection();
    return (React.createElement(Container, { ...props, ref: ref, show: show, role: "dialog", ...aria({ modal: false, describedby: `${id}-${TooltipLabelId}` }) },
        React.createElement(Arrow, { placement: placement, isRTL: isRTL }),
        React.createElement(StyledLabel, { id: `${id}-${TooltipLabelId}`, as: "span" }, props.label)));
});
Tooltip.displayName = 'Tooltip';
const Container = styled.div((props) => css(css `
      color: ${props.theme.tooltip.color};
      background-color: ${props.theme.tooltip.backgroundColor};
      padding: ${Spacings.medium};
      position: relative;
      line-height: 1;
      text-align: start;
      max-width: 240px;
      display: inline-block;
    `, !props.show &&
    css `
        inset-block-start: -100%;
        inset-inline-start: -100%;
        opacity: 0;
      `));
const Arrow = styled.span(({ theme, placement, isRTL }) => {
    const triangleSideLength = createCustomSpacing(5 / 16);
    return css(css `
      color: ${theme.tooltip.backgroundColor};
      position: absolute;

      &:before {
        width: 0;
        height: 0;
        margin: auto;
        content: '';
        display: block;
        border-style: solid;
      }
    `, placement === Positions.bottomMiddle ||
        (placement === Positions.topMiddle &&
            css `
          /* When the Tooltip is centered and not attached to an element, no arrow should be displayed */
          display: none;
        `), (placement === Positions.bottomCenter ||
        placement === Positions.bottomLeft ||
        placement === Positions.bottomRight) &&
        css `
        transform: ${isRTL
            ? `translate(${triangleSideLength}, -${triangleSideLength})`
            : `translate(-${triangleSideLength}, -${triangleSideLength})`};

        &:before {
          border-color: transparent transparent currentcolor transparent;
          border-width: 0 ${triangleSideLength} ${triangleSideLength} ${triangleSideLength};
        }
      `, placement === Positions.bottomCenter &&
        css `
        inset-block-start: 0;
        inset-inline-start: 50%;
      `, placement === Positions.bottomLeft &&
        css `
        inset-block-start: 0;
        inset-inline-start: calc(100% - ${Spacings.small});
      `, placement === Positions.bottomRight &&
        css `
        inset-block-start: 0;
        inset-inline-start: ${Spacings.small};
      `, ((isRTL &&
        (placement === Positions.rightCenter ||
            placement === Positions.rightTop ||
            placement === Positions.rightBottom)) ||
        (!isRTL &&
            (placement === Positions.leftCenter ||
                placement === Positions.leftTop ||
                placement === Positions.leftBottom))) &&
        css `
        transform: translate(${triangleSideLength}, -${triangleSideLength});

        &:before {
          border-color: transparent transparent transparent currentcolor;
          border-width: ${triangleSideLength} 0 ${triangleSideLength} ${triangleSideLength};
        }
      `, placement === Positions.leftCenter &&
        css `
        inset-block-start: 50%;
        inset-inline-end: 0;
      `, placement === Positions.leftTop &&
        css `
        inset-block-start: calc(100% - ${Spacings.small});
        inset-inline-end: 0;
      `, placement === Positions.leftBottom &&
        css `
        inset-block-start: ${Spacings.small};
        inset-inline-end: 0;
      `, (placement === Positions.topCenter ||
        placement === Positions.topRight ||
        placement === Positions.topLeft) &&
        css `
        transform: ${isRTL
            ? `translate(${triangleSideLength}, ${triangleSideLength})`
            : `translate(-${triangleSideLength}, ${triangleSideLength})`};

        &:before {
          border-color: currentcolor transparent transparent transparent;
          border-width: ${triangleSideLength} ${triangleSideLength} 0 ${triangleSideLength};
        }
      `, placement === Positions.topCenter &&
        css `
        inset-block-end: 0;
        inset-inline-start: 50%;
      `, placement === Positions.topRight &&
        css `
        inset-block-end: 0;
        inset-inline-start: ${Spacings.small};
      `, placement === Positions.topLeft &&
        css `
        inset-block-end: 0;
        inset-inline-start: calc(100% - ${Spacings.small});
      `, ((!isRTL &&
        (placement === Positions.rightCenter ||
            placement === Positions.rightTop ||
            placement === Positions.rightBottom)) ||
        (isRTL &&
            (placement === Positions.leftCenter ||
                placement === Positions.leftTop ||
                placement === Positions.leftBottom))) &&
        css `
        transform: translate(-${triangleSideLength}, -${triangleSideLength});

        &:before {
          border-color: transparent currentcolor transparent transparent;
          border-width: ${triangleSideLength} ${triangleSideLength} ${triangleSideLength} 0;
        }
      `, placement === Positions.rightCenter &&
        css `
        inset-block-start: 50%;
        inset-inline-start: 0;
      `, placement === Positions.rightTop &&
        css `
        inset-block-start: calc(100% - ${Spacings.small});
        inset-inline-start: 0;
      `, placement === Positions.rightBottom &&
        css `
        inset-block-start: ${Spacings.small};
        inset-inline-start: 0;
      `);
});
const StyledLabel = styled(Label) `
  color: inherit;
`;
export default Tooltip;
